"use client";
import { useEffect, useRef } from "react";
import { useCurrentUser } from "@zapier/client";
import { useRouterParams } from "../../../hooks/useRouterParams";

export interface LegacyMigrationResult {
  set: Record<string, string>;
  omit: string[];
  /** When true, defer applying (e.g. waiting on the current user to resolve). */
  defer?: boolean;
  hasLegacyParameters: boolean;
}

interface Options {
  /** Pure mapping from current params (+ user) to the migration to apply. */
  migrate: (
    parameters: ReturnType<typeof useRouterParams>,
    user: { id: number } | undefined,
  ) => LegacyMigrationResult;
}

export function useLegacyParameterMigration({ migrate }: Options): {
  hasLegacyParameters: boolean;
} {
  const parameters = useRouterParams();
  const { user } = useCurrentUser();
  const hasApplied = useRef(false);
  const { set, omit, defer, hasLegacyParameters } = migrate(
    parameters,
    user ?? undefined,
  );

  useEffect(() => {
    if (hasApplied.current) return;
    if (!hasLegacyParameters) {
      hasApplied.current = true;
      return;
    }
    if (defer) return;
    hasApplied.current = true;
    parameters.setParams(set, omit);
    // params/set/omit are recomputed each render; the hasApplied ref makes this
    // one-shot, so identity churn in the deps is harmless.
  }, [hasLegacyParameters, defer, set, omit, parameters]);

  return { hasLegacyParameters };
}
